import { Box, Divider, Grid } from '@mui/material'
import { ReactNode, useState } from 'react'
import Popup, { PopupInstance } from 'components/popup/Popup'
import { EventDoc, EventReminder } from 'types/api/changes'
import PopupSection from 'components/popup/PopupSection'
import PopupSectionRow from 'components/popup/PopupSectionRow'
import { CustomReminderContent } from './CustomEventRecurrencePopup'
import { t } from 'i18next'
import i18n from 'i18n'
import dayjs from 'dayjs'
import { isAMPM } from 'utils/date'
import { isTrulyEmpty } from 'utils/object'
import { getDefaultTimezone } from 'index'

interface ContentProps {
  instance: PopupInstance | null | undefined
  reminders: EventReminder[]
  setReminders: (reminders: EventReminder[]) => void
  event?: EventDoc
}

const humanizeDuration = require('humanize-duration')

export function convertMinutesToSeconds(minutes: number) {
  return minutes * 60
}

function getReminderTitle(
  reminder: EventReminder,
  tz: string = getDefaultTimezone()
): string {
  const hour = dayjs(Number(reminder.AlertTime) * 1000)
    .tz(tz)
    .format(isAMPM() ? 'h A' : 'H:mm')

  if (reminder.offset > 0) {
    return `${t('timeBefore', {
      time: minutesToHumanReadable(reminder.offset / 60)
    })} (${hour})`
  }

  return t('onDayOfEvent', {
    time: hour
  })
}

function Content(props: ContentProps) {
  const addReminderToEvent = (reminder: EventReminder) => {
    if (
      props.reminders == undefined ||
      !props.reminders.find((rem) => rem.offset === reminder.offset)
    ) {
      props.setReminders([...(props.reminders ?? []), reminder])
    }

    props.instance?.hide()
  }

  const [showCustom, setShowCustom] = useState(false)

  const allday = props.event?.AllDay === '1'
  const eventStart = props.event?.StartDate // seconds since epoch (midnight for all-day events)

  // For all-day events, StartTime is midnight (00:00) of the event day in seconds
  // We calculate AlertTime as: midnight + 9 hours - (days_before × 24 hours)
  const allDayReminders: EventReminder[] = [
    {
      // On day of event at 9AM: midnight + 9 hours
      AlertTime: eventStart ? String(Number(eventStart) + 9 * 60 * 60) : '',
      isOn: '1',
      isRelativeReminder: '1',
      offset: -9 * 60 * 60
    },
    {
      // 1 day before at 9AM: midnight - 24 hours + 9 hours = midnight - 15 hours
      AlertTime: eventStart
        ? String(Number(eventStart) - 1 * 24 * 60 * 60 + 9 * 60 * 60)
        : '',
      isOn: '1',
      isRelativeReminder: '1',
      offset: convertMinutesToSeconds(24 * 60)
    },
    {
      // 2 days before at 9AM: midnight - 48 hours + 9 hours = midnight - 39 hours
      AlertTime: eventStart
        ? String(Number(eventStart) - 2 * 24 * 60 * 60 + 9 * 60 * 60)
        : '',
      isOn: '1',
      isRelativeReminder: '1',
      offset: convertMinutesToSeconds(2 * 24 * 60)
    },
    {
      // 1 week before at 9AM: midnight - 168 hours + 9 hours = midnight - 159 hours
      AlertTime: eventStart
        ? String(Number(eventStart) - 7 * 24 * 60 * 60 + 9 * 60 * 60)
        : '',
      isOn: '1',
      isRelativeReminder: '1',
      offset: convertMinutesToSeconds(7 * 24 * 60)
    }
  ]

  const defaultReminders: EventReminder[] = [
    {
      AlertTime: '',
      isOn: '1',
      isRelativeReminder: '1',
      offset: 0
    },
    {
      AlertTime: '',
      isOn: '1',
      isRelativeReminder: '1',
      offset: convertMinutesToSeconds(5)
    },
    {
      AlertTime: '',
      isOn: '1',
      isRelativeReminder: '1',
      offset: convertMinutesToSeconds(15)
    },
    {
      AlertTime: '',
      isOn: '1',
      isRelativeReminder: '1',
      offset: convertMinutesToSeconds(30)
    },
    {
      AlertTime: '',
      isOn: '1',
      isRelativeReminder: '1',
      offset: convertMinutesToSeconds(60)
    }
  ]

  return showCustom ? (
    <CustomReminderContent editingReminder={undefined} {...props} />
  ) : (
    <Grid container alignItems={'start'}>
      <PopupSection title={`${t('remindMe')}`}>
        <Box height={8}></Box>
        {(allday ? allDayReminders : defaultReminders)
          .filter((n) => {
            if (!props.reminders) {
              return true
            }
            return props.reminders?.includes(n) == false
          })
          .map((rem) => {
            return (
              <PopupSectionRow
                action={() => {
                  addReminderToEvent(rem)
                }}
                actionId={`action-reminder-${rem.offset}`}
                key={rem.offset}
                title={reminderUserTitle(rem, allday)}
              />
            )
          })}
        <div>
          <ReminderDivider />
          <PopupSectionRow
            action={() => {
              setShowCustom(true)
            }}
            title={t('custom')}
          />
        </div>
      </PopupSection>
    </Grid>
  )
}

interface AddReminderPopupProps {
  children: ReactNode
  open: boolean
  setOpen: (b: boolean) => void
  reminders: EventReminder[]
  setReminders: (reminders: EventReminder[]) => void
  event?: EventDoc
}

export default function AddReminderPopup(props: AddReminderPopupProps) {
  const [instance, onInstance] = useState<PopupInstance>()

  return (
    <Popup
      onMount={onInstance}
      open={props.open}
      setOpen={props.setOpen}
      content={<Content {...props} instance={instance} />}
      placement={'bottom'}
    >
      {props.children}
    </Popup>
  )
}

export function minutesToHumanReadable(seconds: number) {
  let minutes = ''

  if (seconds === 0) {
    return t('onTime')
  }

  try {
    minutes = humanizeDuration(seconds * 60 * 1000, {
      language:
        i18n.language.length > 2 ? i18n.language.slice(0, 2) : i18n.language
    })
  } catch (error) {
    minutes = humanizeDuration(seconds * 60 * 1000)
  }

  return minutes
}

export function reminderUserTitle(reminder: EventReminder, allDay: boolean) {
  if (
    reminder.isRelativeReminder === '0' &&
    !isTrulyEmpty(reminder.AlertTime) &&
    reminder.offset === 0
  ) {
    const alertTime = dayjs(Number(reminder.AlertTime) * 1000).format(
      isAMPM() ? 'MMM D YYYY h:mm A' : 'MMM D YYYY H:mm'
    )

    return `${t('reminderOn')}: ${alertTime}`
  }

  const displayTitle = allDay
    ? getReminderTitle(reminder, allDay ? 'UTC' : getDefaultTimezone())
    : reminder.offset <= 0
      ? t('onTime')
      : `${t('timeBefore', {
          time: minutesToHumanReadable(reminder.offset / 60)
        })}`
  return displayTitle
}

export function ReminderDivider() {
  return (
    <Divider
      sx={{
        borderColor: 'rgb(187,187,187)',
        marginBottom: 1,
        marginTop: '0.4rem'
      }}
    />
  )
}
